import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import useAssistNotes from "../hooks/useAssistNotes";
import useIsMobile from "../hooks/useIsMobile";
import useVisiblePolling from "../hooks/useVisiblePolling";
import { NOTES_STORAGE_KEY, canWriteNotes, initialColumnOpen } from "../lib/assistNotes";
import {
  SESSION_POLL_MS, STATUS_CLASS, STATUS_LABELS, assistError, endedSummary, formatDateTime, isFinished,
  coTechniciansText, formatTimeSeconds, isInvited, isOpen, presentTechnicians, sessionDuration, timelineLabel,
} from "../lib/assist";
import AssistNotesPanel from "../components/assist/AssistNotesPanel";
import DeleteAssistNote from "../components/assist/DeleteAssistNote";
import PharmacyTickets from "../components/tickets/PharmacyTickets";
import { LinkAssistToTicket } from "../components/tickets/TicketLinks";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  MobileSheet, MobileSheetBody, MobileSheetCloseButton, MobileSheetContent, MobileSheetHeader, MobileSheetTitle,
} from "../components/ui/mobile-sheet";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, CheckCircle2, LogOut, Loader2, NotebookPen, PhoneOff, X } from "lucide-react";

// The console carries the LiveKit client: its own chunk, fetched only when a session is actually open.
const AssistConsole = lazy(() => import("../components/assist/AssistConsole"));

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-0.5 sm:gap-3 py-2 border-b border-slate-100 last:border-b-0">
      <dt className="text-xs sm:text-sm text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-900 min-w-0 break-words">{children}</dd>
    </div>
  );
}

const BackLink = () => (
  <Link to="/assistance" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="assist-back">
    <ArrowLeft size={14} />Assistance
  </Link>
);

function readStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// One assistance session, as seen by its technician (the principal, who ends it and reads the code) or by a colleague who
// joined it (multi-technician mode: sees and speaks, can only leave). The state is re-read every few seconds: the pharmacist, the
// system (timeout, deactivation) or another tab can end it at any time, and this page must say so.
export default function AssistSession() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | ready | missing | error
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [ending, setEnding] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const navigate = useNavigate();
  const seq = useRef(0);
  const { user } = useAuth();
  const isMobile = useIsMobile();
  // The shared note: one instance for the column, the full-screen panel, the phone sheet and the report section.
  const notes = useAssistNotes(id, { canEdit: canWriteNotes(user, item), poll: phase === "ready" });
  const [columnOpen, setColumnOpen] = useState(() => initialColumnOpen(readStorage(), window.innerWidth));
  const [sheetOpen, setSheetOpen] = useState(false);
  const toggleNotes = () => {
    if (isMobile) {
      setSheetOpen(true);
      return;
    }
    const next = !columnOpen;
    setColumnOpen(next);
    try {
      readStorage()?.setItem(NOTES_STORAGE_KEY, next ? "1" : "0");
    } catch {
      // storage blocked: the choice lasts for this page only
    }
  };

  const load = useCallback(async ({ silent = false } = {}) => {
    const mine = ++seq.current;
    if (!silent) setPhase("loading");
    try {
      const { data } = await api.get(`/assist/requests/${id}`);
      if (mine !== seq.current) return;
      setItem(data);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      const { status, message } = assistError(e);
      if (status === 404) {
        setPhase("missing");
      } else if (!silent) {
        setError(message);
        setPhase("error");
      }
    }
  }, [id]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);
  // Stable on purpose: the console reconnects when its props change.
  const onSessionEnded = useCallback(() => load({ silent: true }), [load]);
  useVisiblePolling(() => load({ silent: true }), SESSION_POLL_MS, phase === "ready" && !isFinished(item?.status));

  const end = async () => {
    if (ending) return;
    setEnding(true);
    try {
      const { data } = await api.post(`/assist/requests/${id}/end`);
      seq.current += 1;
      setItem(data);
      toast.success("Assistance terminée");
    } catch (e) {
      toast.error(assistError(e, "Impossible de terminer l'assistance. Réessayez.").message);
    } finally {
      setEnding(false);
      setConfirmOpen(false);
    }
  };

  const cancelInvitation = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      const { data } = await api.post(`/assist/invitations/${id}/cancel`);
      seq.current += 1;
      setItem(data);
      toast.success("Invitation annulée");
    } catch (e) {
      toast.error(assistError(e, "Impossible d'annuler l'invitation. Réessayez.").message);
    } finally {
      setCancelling(false);
    }
  };

  const leave = async () => {
    if (leaving) return;
    setLeaving(true);
    try {
      await api.post(`/assist/requests/${id}/leave`);
      toast.success("Vous avez quitté l'assistance");
      navigate("/assistance");
    } catch (e) {
      toast.error(assistError(e, "Impossible de quitter l'assistance. Réessayez.").message);
      setLeaving(false);
      setLeaveOpen(false);
    }
  };

  if (phase === "loading") {
    return (
      <div className="space-y-4" data-testid="assist-session-loading">
        <BackLink /><Skeleton className="h-8 w-64" /><Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (phase === "missing" || phase === "error") {
    return (
      <div className="space-y-4">
        <BackLink />
        <Card className="p-8 text-center space-y-3" data-testid={`assist-session-${phase}`}>
          <AlertTriangle className="mx-auto text-amber-500" size={22} />
          <h3 className="font-medium text-slate-900">{phase === "missing" ? "Assistance introuvable" : "Chargement impossible"}</h3>
          <p className="text-sm text-slate-500">
            {phase === "missing" ? "Cette demande n'existe pas ou n'est pas prise en charge par vous." : error}
          </p>
          {phase === "error" && <Button variant="outline" onClick={() => load()}>Réessayer</Button>}
        </Card>
      </div>
    );
  }

  const open = isOpen(item.status);
  const finished = isFinished(item.status);
  const invited = isInvited(item.status);
  const proposed = item.origin === "technician";
  const principal = item.my_role === "principal";
  const joined = item.my_role === "co";
  const technicians = presentTechnicians(item);
  const withText = coTechniciansText(item);
  const live = open && Boolean(item.my_role);
  const showColumn = live && !isMobile && columnOpen;
  const notesPanel = (prefix) => <AssistNotesPanel notes={notes} idPrefix={prefix} />;
  return (
    <div className="space-y-4" data-testid="assist-session">
      <BackLink />
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display font-bold text-2xl text-slate-900 break-words">{item.pharmacy}</h1>
            <span className={`inline-flex rounded-full px-2 py-0.5 text-2xs font-semibold ${STATUS_CLASS[item.status] || ""}`} data-testid="assist-session-status">
              {STATUS_LABELS[item.status] || item.status}
            </span>
          </div>
          <p className="text-sm text-slate-500">{item.origin === "technician" ? `Proposée à ${item.account || item.requester}` : `Demande de ${item.requester}`}</p>
        </div>
        {live && (
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button variant="outline" onClick={toggleNotes} aria-pressed={isMobile ? undefined : columnOpen} data-testid="assist-notes-toggle">
              <NotebookPen size={16} className="mr-1.5" />Notes
            </Button>
            {principal && (
              <Button variant="destructive" onClick={() => setConfirmOpen(true)} disabled={ending} data-testid="assist-end">
                <PhoneOff size={16} className="mr-1.5" />Terminer l'assistance
              </Button>
            )}
            {joined && (
              <Button variant="outline" onClick={() => setLeaveOpen(true)} disabled={leaving} data-testid="assist-leave">
                <LogOut size={16} className="mr-1.5" />Quitter l'assistance
              </Button>
            )}
          </div>
        )}
      </div>

      {invited && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3" role="status" data-testid="assist-invited">
          <div className="text-sm text-violet-950">
            <p className="font-medium">Invitation envoyée à {item.account || item.requester}, en attente de sa réponse.</p>
            <p className="text-violet-800">
              Si la personne accepte, la console s'ouvre ici et le code de vérification s'affiche. Sans réponse
              {item.expires_at ? ` avant ${formatTimeSeconds(item.expires_at).slice(0, 5)}` : " au bout de 10 minutes"}, la proposition expire.
            </p>
          </div>
          {principal && (
            <Button variant="outline" className="sm:ml-auto shrink-0" onClick={cancelInvitation} disabled={cancelling} data-testid="assist-invite-cancel">
              {cancelling && <Loader2 size={14} className="mr-1.5 animate-spin" />}Annuler l'invitation
            </Button>
          )}
        </div>
      )}

      {finished && (
        <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3" role="status" data-testid="assist-session-ended">
          <CheckCircle2 size={18} className="text-slate-500 mt-0.5 shrink-0" aria-hidden="true" />
          <div className="text-sm">
            <p className="font-medium text-slate-900">{endedSummary(item)}</p>
            <p className="text-slate-500">{item.origin === "technician" ? "Pour une nouvelle assistance, proposez-la de nouveau depuis la page Assistance." : "Une nouvelle assistance nécessitera une nouvelle demande du pharmacien."}</p>
          </div>
        </div>
      )}

      {open && item.verification_code && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3" data-testid="assist-verification">
          <span className="text-sm text-sky-900">Code de vérification</span>
          <span className="font-mono text-2xl font-semibold tracking-[0.3em] text-sky-950" data-testid="assist-verification-code">{item.verification_code}</span>
          <span className="text-sm text-sky-800 sm:ml-auto">Lisez-le au pharmacien au début de l'appel : il s'affiche aussi sur son écran.</span>
        </div>
      )}

      {open && joined && (
        <p className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600" data-testid="assist-joined-note">
          Vous avez rejoint l'assistance de {item.technician?.name} : seul le technicien principal peut la terminer. Vous pouvez la quitter à tout moment.
        </p>
      )}

      {open && technicians.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="assist-present">
          <span className="text-slate-500">Techniciens présents :</span>
          {technicians.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-slate-800">
              {t.name}{t.principal && <span className="text-2xs text-slate-500">(principal)</span>}
            </span>
          ))}
        </div>
      )}

      {live && (
        <div className={showColumn ? "grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_360px]" : ""}>
        <Suspense fallback={<Skeleton className="w-full aspect-video rounded-xl" data-testid="assist-console-loading" />}>
          <AssistConsole requestId={item.id} screenShareExpected={item.screen_share} onSessionEnded={onSessionEnded}
            notesPanel={notesPanel("assist-fs-note")}
            fullscreenInfo={{
              title: item.pharmacy,
              verificationCode: principal ? item.verification_code : null,
              pharmacist: item.operator || item.account || item.requester,
              technicians: technicians.map((t) => t.name),
            }}
            onEnd={principal ? () => setConfirmOpen(true) : undefined}
            onLeave={joined ? () => setLeaveOpen(true) : undefined} />
        </Suspense>
        {showColumn && (
          <aside className="flex max-h-[75vh] flex-col rounded-xl border border-slate-200 bg-white" aria-label="Notes de l'assistance" data-testid="assist-notes-column">
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5">
              <h2 className="text-sm font-semibold text-slate-900">Notes</h2>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleNotes} aria-label="Replier les notes" title="Replier les notes">
                <X size={16} />
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">{notesPanel("assist-col-note")}</div>
          </aside>
        )}
        </div>
      )}

      {isMobile && live && (
        <MobileSheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <MobileSheetContent aria-describedby={undefined} data-testid="assist-notes-sheet">
            <MobileSheetHeader>
              <MobileSheetTitle>Notes de l'assistance</MobileSheetTitle>
              <MobileSheetCloseButton />
            </MobileSheetHeader>
            <MobileSheetBody>{notesPanel("assist-sheet-note")}</MobileSheetBody>
          </MobileSheetContent>
        </MobileSheet>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Demande</h3>
          <dl data-testid="assist-session-details">
            <Row label="Pharmacie">{item.pharmacy || "Non précisée"}</Row>
            {proposed ? (
              <Row label="Proposée à">{item.account || item.requester || "Non précisé"}</Row>
            ) : (
              <>
                <Row label="Demandée par">{item.operator || item.account || item.requester || "Non précisé"}</Row>
                <Row label="Compte connecté">{item.account || "Non précisé"}</Row>
                {item.operator && <Row label="Opérateur (PIN)">{item.operator}</Row>}
              </>
            )}
            <Row label="Motif"><span className="whitespace-pre-line">{item.reason || "Aucun motif"}</span></Row>
            <Row label="Partage possible">{item.screen_share ? "Écran et audio" : "Audio seulement"}</Row>
          </dl>
        </Card>
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-1">Déroulement</h3>
          <dl data-testid="assist-session-progress">
            <Row label="Statut">{STATUS_LABELS[item.status] || item.status}</Row>
            {finished && <Row label="Issue">{endedSummary(item)}</Row>}
            <Row label={withText ? "Technicien principal" : "Technicien"}>{item.technician?.name || "Pas encore attribué"}</Row>
            {withText && <Row label="Autres techniciens">{withText.replace(/^avec /, "")}</Row>}
            <Row label={proposed ? "Proposée le" : "Demandée le"}>{formatDateTime(item.created_at)}</Row>
            {!invited && <Row label={proposed ? "Acceptée le" : "Prise en charge le"}>{formatDateTime(item.accepted_at)}</Row>}
            {!invited && <Row label="Début de la session">{formatDateTime(item.started_at)}</Row>}
            {!invited && <Row label="Fin">{formatDateTime(item.ended_at)}</Row>}
            {!invited && <Row label="Durée">{finished ? sessionDuration(item) : "En cours"}</Row>}
          </dl>
        </Card>
      </div>

      {!live && !invited && item.technician && (
        <Card className="p-4" data-testid="assist-report">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-900">Compte rendu</h3>
            {notes.note && <DeleteAssistNote requestId={item.id} onDeleted={() => navigate("/assistance?tab=notes")} />}
          </div>
          <div className="max-w-3xl">{notesPanel("assist-report-note")}</div>
        </Card>
      )}

      {!live && item.technician && (
        <PharmacyTickets linkedTo={{ assist_id: item.id }} newTicketLink={`/support/tickets/nouveau?assist=${encodeURIComponent(item.id)}`}
          extra={(reload) => <LinkAssistToTicket assistId={item.id} onLinked={reload} />} />
      )}

      {item.timeline?.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-slate-900 mb-2">Chronologie</h3>
          <ol className="space-y-1.5" data-testid="assist-session-timeline">
            {item.timeline.map((entry, index) => (
              <li key={`${entry.at}-${entry.event}-${index}`} className="flex gap-3 text-sm">
                <span className="w-20 shrink-0 text-slate-500 tabular-nums">{formatTimeSeconds(entry.at)}</span>
                <span className="text-slate-800">{timelineLabel(entry)}</span>
              </li>
            ))}
          </ol>
        </Card>
      )}

      <AlertDialog open={leaveOpen} onOpenChange={(value) => !leaving && setLeaveOpen(value)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Quitter l'assistance avec {item.pharmacy} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous ne verrez plus l'écran du pharmacien. L'assistance continue avec {item.technician?.name} ; vous pourrez la rejoindre à nouveau
              tant qu'elle est en cours et qu'il reste de la place.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={leaving}>Rester</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); leave(); }} disabled={leaving} data-testid="assist-leave-confirm">
              {leaving && <Loader2 size={14} className="mr-1.5 animate-spin" />}Quitter
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmOpen} onOpenChange={(value) => !ending && setConfirmOpen(value)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Terminer l'assistance avec {item.pharmacy} ?</AlertDialogTitle>
            <AlertDialogDescription>
              {technicians.length > 1 ? "Le pharmacien, vous et les autres techniciens serez" : "Le pharmacien et vous serez"} déconnectés immédiatement. Cette assistance ne pourra pas être reprise : il faudra une nouvelle demande.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={ending}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); end(); }} disabled={ending} className="bg-red-600 hover:bg-red-700" data-testid="assist-end-confirm">
              {ending && <Loader2 size={14} className="mr-1.5 animate-spin" />}Terminer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
